import { WORD_COUNT_OPTIONS } from '../../types/story';

interface WordCountSelectorProps {
  selectedWordCount: 2500 | 5000;
  onSelectWordCount: (count: 2500 | 5000) => void;
  userCreditBalance: number;
}

export default function WordCountSelector({
  selectedWordCount,
  onSelectWordCount,
  userCreditBalance,
}: WordCountSelectorProps) {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-display font-bold text-gray-900 mb-2">
          Choose Story Length
        </h2>
        <p className="text-gray-600">
          Select the perfect length for your audiobook
        </p>
      </div>

      {/* Credit Balance Display */}
      <div className="p-4 bg-primary-50 border-2 border-primary-200 rounded-xl text-center">
        <p className="text-sm text-primary-800">
          Your Credit Balance:{' '}
          <span className="font-bold text-2xl text-primary-600">
            {userCreditBalance}
          </span>{' '}
          {userCreditBalance === 1 ? 'credit' : 'credits'}
        </p>
      </div>

      {/* Word Count Options */}
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {WORD_COUNT_OPTIONS.map((option) => {
          const hasEnoughCredits = userCreditBalance >= option.credits;

          return (
            <button
              key={option.value}
              onClick={() => hasEnoughCredits && onSelectWordCount(option.value)}
              disabled={!hasEnoughCredits}
              className={`
                relative p-8 rounded-2xl border-2 text-center transition-all duration-200
                ${
                  selectedWordCount === option.value
                    ? 'border-primary-500 bg-primary-50 shadow-soft-lg scale-105'
                    : hasEnoughCredits
                    ? 'border-gray-200 hover:border-primary-300 hover:shadow-soft'
                    : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                }
              `}
            >
              {/* Selected indicator */}
              {selectedWordCount === option.value && (
                <div className="absolute top-4 right-4">
                  <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
              )}

              {/* Not enough credits badge */}
              {!hasEnoughCredits && (
                <div className="absolute top-4 right-4">
                  <div className="px-3 py-1 bg-red-100 border border-red-300 rounded-full">
                    <p className="text-xs font-semibold text-red-700">
                      Need {option.credits - userCreditBalance} more
                    </p>
                  </div>
                </div>
              )}

              {/* Credit Cost Badge */}
              <div className="mb-4">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${
                    selectedWordCount === option.value
                      ? 'bg-gradient-to-br from-primary-400 to-primary-600'
                      : 'bg-gradient-to-br from-gray-300 to-gray-400'
                  }`}
                >
                  <span className="text-3xl font-display font-bold text-white">
                    {option.credits}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {option.credits === 1 ? 'credit' : 'credits'}
                </p>
              </div>

              {/* Label */}
              <h3 className="text-2xl font-display font-bold text-gray-900 mb-2">
                {option.label}
              </h3>

              {/* Word Count */}
              <p className="text-lg text-primary-600 font-semibold mb-2">
                {option.value.toLocaleString()} words
              </p>

              {/* Duration */}
              <div className="flex items-center justify-center gap-2 mb-3">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-sm text-gray-600">{option.duration}</span>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600">{option.description}</p>
            </button>
          );
        })}
      </div>

      {/* Low Credits Warning */}
      {userCreditBalance < 2 && (
        <div className="p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl max-w-2xl mx-auto">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⚠️</span>
            <div>
              <h4 className="font-semibold text-yellow-900 mb-1">
                Low on Credits
              </h4>
              <p className="text-sm text-yellow-800">
                You have {userCreditBalance}{' '}
                {userCreditBalance === 1 ? 'credit' : 'credits'} remaining.
                {userCreditBalance === 0 && ' Purchase more credits to create stories.'}
              </p>
              <button className="mt-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-semibold transition-colors">
                Purchase Credits
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
